import React, { useState, useMemo, useEffect, useRef } from 'react'
import { IconButton, Typography, Chip, Box } from '@mui/material'
import { Button } from '@/@core/components/mui/Button'
import dayjs from 'dayjs'
import { useRouteQuery, useTranslator } from '@/hooks'
import { AttendanceStatusEnum, BalanceStatusEnum } from '@/@data'

interface FilterMonthAttendanceProps {
  selectedDate?: Date
  onDateChange?: (dateString: string) => void
  status?: string
  className?: string
  isShowHistory?: boolean
  onShowHistory?: () => void
  showToday?: boolean
  data?: any
  isShowWorkingRate?: boolean
  isShowSettingDay?: boolean
  currentPayPeriod?: string
  isHideDay?: boolean
  isChooseFuture?: boolean
  isDisablePreviousMonth?: boolean
}

const FilterMonthAttendance: React.FC<FilterMonthAttendanceProps> = ({
  selectedDate,
  onDateChange,
  status = '',
  className = '',
  isShowHistory = false,
  onShowHistory,
  showToday = true,
  data = {},
  isShowWorkingRate = false,
  isShowSettingDay = false,
  isHideDay = false,
  isChooseFuture = false,
  currentPayPeriod,
  isDisablePreviousMonth = false
}) => {
  const { __ } = useTranslator()
  const { updateQuery, searchQuery } = useRouteQuery()
  const monthFromUrl = searchQuery.get('month')

  const getInitialDate = () => {
    const hasValidPayPeriod = !!currentPayPeriod && dayjs(currentPayPeriod).isValid()
    const hasValidMonthFromUrl = !!monthFromUrl && dayjs(monthFromUrl).isValid()

    if (hasValidPayPeriod) {
      return dayjs(currentPayPeriod as string).toDate()
    }

    if (hasValidMonthFromUrl) {
      return dayjs(monthFromUrl as string).toDate()
    }

    if (selectedDate) {
      return selectedDate
    }

    return new Date()
  }

  const [currentDate, setCurrentDate] = useState<Date>(getInitialDate())
  const [currentStatus, setCurrentStatus] = useState<string>(status || '')
  const updateQueryRef = useRef(updateQuery)
  const previousPayPeriodRef = useRef<string | undefined>(undefined)
  const previousCompanyIdRef = useRef<string | undefined>(undefined)
  const isUserNavigatingRef = useRef(false)

  updateQueryRef.current = updateQuery

  // Reset navigation flag when company changes
  const currentCompanyId = searchQuery.get('companyId') || undefined
  const isCompanyChangedRef = useRef(false)

  useEffect(() => {
    if (previousCompanyIdRef.current !== currentCompanyId && previousCompanyIdRef.current !== undefined) {
      isUserNavigatingRef.current = false
      previousPayPeriodRef.current = undefined
      isCompanyChangedRef.current = true
    }

    previousCompanyIdRef.current = currentCompanyId
  }, [currentCompanyId])

  useEffect(() => {
    setCurrentStatus(status || '')
  }, [status, data])

  useEffect(() => {
    if (!currentPayPeriod || !dayjs(currentPayPeriod).isValid()) {
      return
    }

    // Update when:
    const isCompanyChanged = isCompanyChangedRef.current

    const shouldUpdate =
      previousPayPeriodRef.current !== currentPayPeriod &&
      !isUserNavigatingRef.current &&
      (isCompanyChanged || !monthFromUrl)

    if (shouldUpdate) {
      const newMonth = dayjs(currentPayPeriod).startOf('month').format('YYYY-MM-DD')

      setCurrentDate(dayjs(currentPayPeriod).toDate())
      updateQueryRef.current({ month: newMonth })
      previousPayPeriodRef.current = currentPayPeriod
      isCompanyChangedRef.current = false // Reset flag after using
    } else if (previousPayPeriodRef.current !== currentPayPeriod) {
      // Just update the ref without changing the date if user has navigated
      previousPayPeriodRef.current = currentPayPeriod
      isCompanyChangedRef.current = false // Reset flag
    }

    isUserNavigatingRef.current = false
  }, [currentPayPeriod, monthFromUrl])

  useEffect(() => {
    if (!monthFromUrl || !dayjs(monthFromUrl).isValid()) return

    if (!isUserNavigatingRef.current) {
      setCurrentDate(dayjs(monthFromUrl).toDate())
    }
  }, [monthFromUrl])

  const currentMonth = useMemo(() => {
    return dayjs(currentDate).format('MMMM YYYY')
  }, [currentDate])

  const dateRange = useMemo(() => {
    const start = dayjs(currentDate).startOf('month')
    const end = dayjs(currentDate).endOf('month')

    return {
      start: start.format('DD/MM/YYYY'),
      end: end.format('DD/MM/YYYY')
    }
  }, [currentDate])

  useEffect(() => {
    // Only set initial month if there's no month in URL and user hasn't navigated yet
    if (!searchQuery.get('month') && !isUserNavigatingRef.current) {
      const hasValidPayPeriod = !!currentPayPeriod && dayjs(currentPayPeriod).isValid()

      if (hasValidPayPeriod) {
        const month = dayjs(currentPayPeriod as string)
          .startOf('month')
          .format('YYYY-MM-DD')

        updateQueryRef.current({ month })
      } else {
        // Fallback: if no currentPayPeriod yet, set to current month
        const month = dayjs().startOf('month').format('YYYY-MM-DD')

        updateQueryRef.current({ month })
      }
    }
  }, [currentPayPeriod, searchQuery])

  const isCurrentMonth = useMemo(() => {
    const hasValidPayPeriod = !!currentPayPeriod && dayjs(currentPayPeriod).isValid()
    const baseDate = hasValidPayPeriod ? dayjs(currentPayPeriod as string) : dayjs()

    return dayjs(currentDate).isSame(baseDate, 'month')
  }, [currentDate, currentPayPeriod])

  const handlePreviousMonth = () => {
    isUserNavigatingRef.current = true

    // Ensure we're working with a valid date
    const baseDate = dayjs(currentDate).isValid() ? dayjs(currentDate) : dayjs()
    const newDate = baseDate.subtract(1, 'month')

    setCurrentDate(newDate.toDate())

    onDateChange?.(newDate.startOf('month').format('YYYY-MM-DD'))
  }

  const handleNextMonth = () => {
    isUserNavigatingRef.current = true

    // Ensure we're working with a valid date
    const baseDate = dayjs(currentDate).isValid() ? dayjs(currentDate) : dayjs()
    const newDate = baseDate.add(1, 'month')

    setCurrentDate(newDate.toDate())

    onDateChange?.(newDate.startOf('month').format('YYYY-MM-DD'))
  }

  const handleToday = () => {
    isUserNavigatingRef.current = true

    if (currentPayPeriod) {
      const today = dayjs(currentPayPeriod)

      setCurrentDate(today.toDate())

      onDateChange?.(today.startOf('month').format('YYYY-MM-DD'))
    } else {
      const today = dayjs()

      setCurrentDate(today.toDate())

      onDateChange?.(today.startOf('month').format('YYYY-MM-DD'))
    }
  }

  useEffect(() => {
    if (!isUserNavigatingRef.current) return

    const newMonth = dayjs(currentDate).startOf('month').format('YYYY-MM-DD')

    if (monthFromUrl !== newMonth) {
      updateQueryRef.current({ month: newMonth })
    }

    isUserNavigatingRef.current = false
  }, [currentDate, monthFromUrl])

  return (
    <Box className={`flex items-center justify-between bg-white ${className}`}>
      <div className='flex items-center gap-3'>
        <Box className='flex items-center gap-3'>
          <IconButton
            onClick={() => {
              handlePreviousMonth()
              updateQueryRef.current({ page: 1 })
            }}
            size='small'
            className='text-gray-600 hover:text-gray-800'
            disabled={isDisablePreviousMonth}
          >
            <i className='tabler-chevron-left' />
          </IconButton>

          <IconButton
            onClick={() => {
              handleNextMonth()
              updateQueryRef.current({ page: 1 })
            }}
            size='small'
            className='text-gray-600 hover:text-gray-800'
            disabled={isCurrentMonth && !isChooseFuture}
          >
            <i className='tabler-chevron-right' />
          </IconButton>

          <Typography variant='h6' className='text-gray-800 font-medium text-[24px]'>
            {currentMonth}
          </Typography>
        </Box>

        <div className='flex items-center gap-3'>
          {currentStatus && (
            <Chip
              label={currentStatus === 'partial' ? 'pay partial' : currentStatus}
              size='small'
              className='capitalize rounded-full p-4'
              classes={
                currentStatus === AttendanceStatusEnum.Unreconciled
                  ? {
                      root: 'bg-red-50 text-red-600 border-red-200',
                      label: 'text-red-600'
                    }
                  : currentStatus === AttendanceStatusEnum.Reconciled
                    ? {
                        root: 'bg-green-50 text-green-600 border-green-200',
                        label: 'text-green-600'
                      }
                    : currentStatus === AttendanceStatusEnum.Checking
                      ? {
                          root: 'bg-blue-50 text-blue-600 border-blue-200',
                          label: 'text-blue-600'
                        }
                      : currentStatus === BalanceStatusEnum.Paid
                        ? {
                            root: 'bg-[#E8F5E8] text-[#28C76F] border-green-200',
                            label: 'text-[#28C76F]'
                          }
                        : currentStatus === BalanceStatusEnum.Pending
                          ? {
                              root: 'bg-[#FDF0E1] text-[#fff] border-blue-200',
                              label: 'text-[#F69F43]'
                            }
                          : currentStatus === BalanceStatusEnum.Partial
                            ? {
                                root: 'bg-[#D6F4F8] text-[#FFF3E0] border-orange-200',
                                label: 'text-[#45BAD1]'
                              }
                            : currentStatus === BalanceStatusEnum.Unpaid
                              ? {
                                  root: 'bg-[#FFEBEE] text-[#FFEBEE] border-orange-200',
                                  label: 'text-[#EA5455]'
                                }
                              : currentStatus === 'estimate balance'
                                ? {
                                    root: 'bg-[#CFD5FC] text-[#3551F2] border-blue-200',
                                    label: 'text-[#3551F2]'
                                  }
                                : {
                                    root: 'bg-gray-50 text-gray-600 border-gray-200',
                                    label: 'text-gray-600'
                                  }
              }
              variant='tonal'
            />
          )}

          {currentStatus === AttendanceStatusEnum.Unreconciled && isShowWorkingRate && (
            <div className='flex items-center gap-2 bg-[#97939e] py-1 px-3 rounded-full'>
              <i className='tabler-calendar-event text-[#fff]' style={{ fontSize: 16 }} />
              <span className='text-[14px] font-medium text-[#fff]'>
                {__('Assumption Working Rate')} {Number(data?.company?.working_rate)}%
              </span>
            </div>
          )}
        </div>
      </div>

      <Box className='flex items-center gap-3'>
        {!isShowSettingDay && !isHideDay && (
          <Button variant='outlined' size='small' className='border-blue-500 text-blue-600 hover:border-blue-600'>
            <span className='text-[15px] font-medium'>
              {dateRange.start} - {dateRange.end}
            </span>
          </Button>
        )}

        {isShowSettingDay && !!data?.attendance?.startDate && !!data?.attendance?.endDate && (
          <Button variant='outlined' size='small' className='border-blue-500 text-blue-600 hover:border-blue-600'>
            <span className='text-[15px] font-medium'>
              {dayjs(data?.attendance?.startDate).format('DD/MM/YYYY')} -{' '}
              {dayjs(data?.attendance?.endDate).format('DD/MM/YYYY')}
            </span>
          </Button>
        )}

        {showToday && (
          <Button variant='tonal' size='small' color='primary' onClick={handleToday} disabled={isCurrentMonth}>
            {__('Today')}
          </Button>
        )}

        {isShowHistory && (
          <Button
            variant='tonal'
            size='small'
            color='primary'
            startIcon={<i className='tabler-history' />}
            className='bg-[#00bad129] text-[#00bad1]'
            onClick={onShowHistory}
          >
            {__('History')}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default FilterMonthAttendance
